import { TERMS_OF_SERVICE_METADATA } from "atlas_forecasting_app/content/legal_document_metadata"

export const TERMS_OF_SERVICE_TITLE = TERMS_OF_SERVICE_METADATA.title
export const TERMS_OF_SERVICE_LAST_UPDATED = TERMS_OF_SERVICE_METADATA.last_updated

export const TERMS_OF_SERVICE_MARKDOWN = `
These Terms of Service (the "Terms") are a legally binding agreement between you and Atlas Forecasting, Inc. ("Atlas," "we," "us," or "our") governing your access to and use of https://atlasforecasting.com/, the Atlas Forecasting web application, and any related products, features, content, and services we make available (collectively, the "Service").

These Terms apply whether you use the public portions of the Site or create an account and use authenticated features such as question drafting, question import, public/private visibility controls, wallet funding, and paid forecast requests.

By accessing or using the Service, clicking to accept these Terms, or creating an Atlas account, you agree to these Terms. If you do not agree, do not use the Service.

## 1. The Service

Atlas Forecasting is a forecasting platform. The Service includes features that allow users to:

- Sign in with Google.
- Create, refine, or import forecasting questions.
- Choose whether to make questions and forecasts public or private.
- Fund an internal Atlas wallet balance through Stripe.
- Spend wallet balance to request AI-assisted forecasts and review forecast history.
- View public questions and forecasts through public or shared parts of the Service.

The Service is experimental and may be in an early or evolving stage. It may change at any time and may include bugs, interruptions, outages, errors, delays, omissions, failed jobs, or incomplete functionality. Atlas does not guarantee uptime, availability, or uninterrupted access to the Service.

## 2. Eligibility and Accounts

You may use the Service only if:

- You are at least 13 years old.
- If you are under 18, your parent or legal guardian has reviewed and agrees to these Terms on your behalf.
- You are not barred from using the Service under applicable law.
- You are not located in, ordinarily resident in, or using the Service from a country or region subject to U.S. sanctions or embargoes, and you are not listed on any U.S. government denied-party or restricted-party list.
- Any information you provide to us is accurate and kept reasonably up to date.
- If you use the Service on behalf of a company or other entity, you have authority to bind that entity to these Terms.

You are responsible for all activity that occurs under your account and for maintaining the security of your account. You must promptly notify us if you believe your account has been compromised or used without authorization.

## 3. No Professional Advice; No Forecast Guarantees

Atlas provides forecasting tools, AI-generated outputs, summaries, write-ups, code, and other content for informational purposes only.

Atlas does **not** provide legal, financial, investment, tax, accounting, medical, employment, compliance, or other professional advice. You should not treat any Atlas output as a recommendation to take or avoid any action, and you should not rely on the Service as your sole source of information for decisions with financial, legal, personal, or operational consequences.

You acknowledge that you are solely responsible for any decisions you make based on the Service and that you do not rely on Atlas as a substitute for independent judgment. Atlas does not act as a fiduciary, advisor, or agent for you.

Forecasts, probabilities, narratives, calculations, and other outputs may be inaccurate, incomplete, stale, biased, misleading, or unavailable. Atlas does not guarantee that any forecast, question draft, imported question, or other output will be correct, reliable, useful, timely, uninterrupted, available, or fit for your purposes.

## 4. Acceptable Use

You may use the Service only in compliance with these Terms and applicable law. You may not, and may not help others to:

- Use the Service for unlawful, fraudulent, deceptive, harassing, abusive, defamatory, infringing, or otherwise harmful purposes.
- Interfere with or disrupt the Service, including by attempting to overload, probe, bypass, or defeat access controls, rate limits, billing controls, security measures, or usage restrictions.
- Reverse engineer, extract, scrape, harvest, benchmark without authorization, or otherwise attempt to extract underlying models, prompts, source code, training data, system behavior, hidden evaluation criteria, or other non-public system details from the Service or from third-party providers used by the Service.
- Use bots, scripts, scraping tools, bulk-export tools, or similar methods to collect Site data, user content, forecasts, or other Service content except through features we expressly make available for that purpose.
- Upload, submit, or transmit malware, exploit code, spam, or other harmful material.
- Use the Service to infringe intellectual-property, privacy, publicity, confidentiality, or other rights.
- Submit content that you do not have the right to submit and authorize Atlas to process.
- Submit confidential, sensitive, export-controlled, regulated, or personal information if you do not want Atlas, its personnel, or its service providers to access or process it, or if you lack a lawful basis to share it.
- Misrepresent your identity, affiliation, or authority.

We may investigate suspected misuse and cooperate with law enforcement or other third parties when appropriate.

Atlas has no obligation to monitor user activity or User Content, but may do so at its discretion.

## 5. User Content

As between you and Atlas, you retain ownership of the prompts, draft messages, question specifications, imported post identifiers you provide, questions you create, and other content you submit to the Service ("User Content").

You grant Atlas and its affiliates, contractors, and service providers a worldwide, non-exclusive, royalty-free license to host, store, reproduce, modify, adapt, process, transmit, use, display, perform, distribute, and create derivative works from User Content as needed to:

- Operate, maintain, secure, debug, support, and improve the Service.
- Provide question drafting, import, forecasting, billing, moderation, and related functionality.
- Share User Content with service providers that help us provide the Service.
- Display User Content within the Service, including on public or shared portions of the Service in accordance with your selected visibility settings and the Service's functionality.
- Enforce our policies and comply with law.

You represent and warrant that you have all rights needed to submit User Content and grant the license above.

Atlas lets users choose whether questions and forecasts are public or private. We will use commercially reasonable efforts to apply the visibility settings you choose. However, the Service does not promise absolute confidentiality, and you should not submit material you cannot risk being processed by Atlas and its providers or, where content is public, shared, or disclosed by mistake, viewed by others.

We do not guarantee that visibility settings will prevent all access to or disclosure of your content.

Subject to these Terms, Atlas grants you a limited, non-exclusive, revocable right to use outputs generated for you through the Service. Atlas may retain, use, reproduce, analyze, and display outputs as needed to operate, maintain, secure, debug, support, and improve the Service.

## 6. Feedback

If you give Atlas suggestions, ideas, enhancement requests, or other feedback about the Service, you grant Atlas a worldwide, perpetual, irrevocable, transferable, sublicensable, royalty-free right to use that feedback for any lawful purpose without restriction or compensation to you.

## 7. Atlas Intellectual Property

The Service, including its software, workflows, interfaces, branding, trademarks, logos, site design, compilations, and all related intellectual-property rights, is owned by Atlas or its licensors.

These Terms do not give you any ownership rights in the Service. Subject to your compliance with these Terms, Atlas grants you a limited, non-exclusive, non-transferable, revocable right to use the Service for its intended purpose.

## 8. Payments, Wallet Credits, Forecast Charges, Refunds, and Disputes

### Atlas wallet credits

Atlas may let you add funds to an internal wallet balance through Stripe. Wallet balances are prepaid internal service credits only. They are not bank accounts, payment accounts, deposit accounts, stored-value accounts, or gift cards, and they do not earn interest.

Except where required by law, wallet credits:

- Have no cash value.
- Are non-transferable.
- May be used only within the Service for eligible Atlas offerings.

### Forecast pricing and charges

Atlas may charge fees for forecasts or other paid features. Current pricing is displayed within the Service, and Atlas may change prices, available products, wallet minimums or maximums, or charging rules prospectively.

When you request a forecast, you are paying Atlas to process the request, not for any particular outcome. The charge is not a guarantee that:

- A forecast will complete successfully.
- A forecast will be accurate, useful, or on time.
- The Service will remain available or uninterrupted.

Unless Atlas states otherwise in writing, forecast charges are final once the forecast request is accepted for processing, except where required by law or where Atlas elects to correct a billing error, duplicate charge, or fraudulent transaction.

You are responsible for any taxes, duties, levies, or similar governmental charges associated with your purchases or use of paid features, other than taxes based on Atlas's net income.

### Stripe processing and top-ups

Payments are processed by Stripe and may be subject to Stripe's own terms, policies, and payment-network rules. Atlas credits wallet funds only after Stripe or our records confirm the payment.

Atlas may decline, delay, reverse, or withhold wallet credits if a payment is not completed, is later reversed, is disputed, is suspected to be fraudulent, or otherwise creates risk for Atlas or others.

### Refunds, chargebacks, and negative balances

Except where required by law, wallet top-ups are final after payment is credited, unless Atlas chooses to issue a refund or reversal in its discretion or to correct billing problems, duplicate transactions, or fraud.

If a top-up is refunded, reversed, or disputed through Stripe or the payment network, Atlas may remove the corresponding wallet credits. This may cause your wallet balance to become negative. If your balance is negative, Atlas may suspend or limit your ability to request new forecasts or use paid features until the balance is restored.

You remain responsible for amounts you owe Atlas, including negative balances caused by chargebacks, disputes, reversals, or misuse.

## 9. Third-Party Services

The Service depends on third-party providers, including Google for authentication, Stripe for payment processing, OpenAI and other AI providers for AI-assisted functionality, and Metaculus and Kalshi for question imports requested by users.

Atlas is not responsible for third-party services, their availability, or their acts or omissions. Your use of third-party services may be subject to separate terms and privacy policies from those providers.

## 10. Suspension and Termination

Atlas reserves the right to refuse service to anyone for any reason at any time, to the maximum extent permitted by law.

Atlas may suspend, restrict, disable, or terminate your access to all or part of the Service, remove or refuse content, cancel or refuse transactions, or stop offering any feature at any time, with or without notice, if:

- We believe you violated these Terms or applicable law.
- We believe your use creates risk, harm, liability, or security issues for Atlas, users, service providers, or others.
- We suspect fraud, abuse, payment issues, or unauthorized activity.
- We are required to do so by law or third-party provider requirements.
- We decide to change, discontinue, or limit the Service as part of operating the business.

You may stop using the Service at any time. Sections that by their nature should survive termination will survive, including those on payments, User Content licenses, feedback, intellectual property, disclaimers, limitations of liability, indemnity, dispute resolution, and general terms.

## 11. Copyright Complaints

If you believe content on the Service infringes your copyright, send a notice with enough detail for us to review it, including your contact information, identification of the copyrighted work, identification of the allegedly infringing material, and a statement that you have a good-faith belief the use is unauthorized.

Send copyright notices to:

- legal@atlasforecasting.com
- 14001 W State Hwy 29 Suite 102, Liberty Hill, TX, 78642

Atlas may remove or disable allegedly infringing content and may terminate repeat infringers where appropriate.

## 12. Disclaimer of Warranties

TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." ATLAS AND ITS AFFILIATES, LICENSORS, AND SERVICE PROVIDERS DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, QUIET ENJOYMENT, ACCURACY, OR THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF HARMFUL COMPONENTS.

WITHOUT LIMITING THE FOREGOING, THE SERVICE MAY CONTAIN ERRORS, OMISSIONS, OR INCOMPLETE FUNCTIONALITY, AND ATLAS DOES NOT WARRANT THAT ANY FORECAST, IMPORTED QUESTION, AI-GENERATED DRAFT, WRITE-UP, CALCULATION, OR OTHER OUTPUT WILL BE CORRECT, COMPLETE, RELIABLE, AVAILABLE, OR SUITABLE FOR YOUR USE.

## 13. Indemnity

To the maximum extent permitted by law, you will defend, indemnify, and hold harmless Atlas, its affiliates, officers, directors, employees, contractors, and licensors from and against any claims, liabilities, damages, judgments, losses, costs, and expenses, including reasonable attorneys' fees, arising out of or related to:

- Your User Content.
- Your use of the Service.
- Your violation of these Terms or applicable law.
- Your infringement or violation of another person's rights.
- Your fraud, misconduct, or unauthorized use of the Service.

## 14. Limitation of Liability

TO THE MAXIMUM EXTENT PERMITTED BY LAW:

- ATLAS AND ITS AFFILIATES, LICENSORS, AND SERVICE PROVIDERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, BUSINESS, GOODWILL, DATA, OR USE.
- THE AGGREGATE LIABILITY OF ATLAS AND ITS AFFILIATES, LICENSORS, AND SERVICE PROVIDERS FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THE SERVICE OR THESE TERMS WILL NOT EXCEED THE GREATER OF (A) $100 OR (B) THE AMOUNTS YOU PAID TO ATLAS FOR THE SERVICE IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM.

These limitations apply even if a remedy fails of its essential purpose and even if Atlas was advised of the possibility of the damages. Some jurisdictions do not allow certain disclaimers or limitations, so parts of this section may not apply to you to the extent prohibited by law.

## 15. Dispute Resolution; Arbitration Agreement

Please read this section carefully. Subject to the limited exceptions below, it requires you and Atlas to resolve disputes through binding, individual arbitration and waives the right to a jury trial and to participate in class or representative actions.

### Informal dispute resolution

Before starting arbitration or filing a claim in court, you and Atlas agree to try to resolve any dispute informally. The party starting the dispute must send written notice describing the claim, the requested relief, and contact information.

Atlas notice contact:

- legal@atlasforecasting.com
- 14001 W State Hwy 29 Suite 102, Liberty Hill, TX, 78642

You and Atlas agree to work in good faith to resolve the dispute for at least 60 days after valid notice is received before starting formal proceedings.

### Arbitration agreement

Except for disputes that qualify for small claims court and except for claims seeking injunctive or equitable relief for alleged intellectual-property infringement, misuse, or unauthorized access to the Service, any dispute, claim, or controversy arising out of or relating to these Terms or the Service will be resolved by binding arbitration administered by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules, as modified by these Terms.

Arbitration will be conducted in English. If an in-person hearing is required, it will take place in the county where you reside, or another location the parties agree to, unless the applicable AAA rules permit remote proceedings.

The arbitrator, and not a court or jury, will have exclusive authority to resolve disputes about the interpretation, applicability, enforceability, or formation of this arbitration agreement, except that a court may decide issues relating to the enforceability of the class-action waiver below.

### Class-action and jury-trial waivers

YOU AND ATLAS AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF, CLASS MEMBER, OR REPRESENTATIVE IN ANY PURPORTED CLASS, COLLECTIVE, CONSOLIDATED, PRIVATE-ATTORNEY-GENERAL, OR REPRESENTATIVE PROCEEDING.

TO THE MAXIMUM EXTENT PERMITTED BY LAW, YOU AND ATLAS WAIVE ANY RIGHT TO A JURY TRIAL.

### Small claims court

Either party may bring an eligible individual claim in small claims court instead of arbitration if the claim remains individual and otherwise qualifies.

### Governing law

These Terms and any dispute between you and Atlas will be governed by the laws of the State of Texas, without regard to conflict-of-laws rules, except that the Federal Arbitration Act governs the interpretation and enforcement of the arbitration agreement above.

### Severability

If any part of this dispute-resolution section is found unenforceable, that part will be severed and the rest will remain in effect, except that if the class-action waiver is found unenforceable for a claim or requested remedy, that claim or remedy will proceed in court and not in arbitration.

## 16. Changes to the Service and to These Terms

Atlas may modify, suspend, or discontinue all or any part of the Service at any time.

Atlas may also update these Terms from time to time. If we make a material adverse change, we will use reasonable efforts to provide at least 30 days' advance notice by posting the updated Terms, updating the "Last updated" date, sending email, providing in-product notice, or using another reasonable method.

Changes addressing new features, legal requirements, administrative matters, or security issues may become effective immediately or on the date stated in the updated Terms. Your continued use of the Service after updated Terms become effective means you accept the updated Terms.

## 17. General Terms

- **Electronic communications.** You agree that we may send notices and other communications electronically, including by email, in-product notice, or posting on the Site.
- **Assignment.** You may not assign or transfer these Terms or your rights under them without Atlas's prior written consent. Atlas may assign these Terms in connection with a merger, acquisition, corporate reorganization, sale of assets, or by operation of law.
- **Force majeure.** Atlas is not liable for delays, interruptions, or failures arising from events beyond its reasonable control, including internet or telecommunications failures, outages by hosting providers, cloud providers, AI providers, or payment processors, utility failures, labor disputes, natural disasters, severe weather, public-health events, war, terrorism, civil unrest, cyberattacks, or governmental action.
- **No waiver.** A failure to enforce any provision is not a waiver of future enforcement.
- **Severability.** Except as stated in the dispute-resolution section, if any provision of these Terms is held unenforceable, the remaining provisions will remain in full force and effect.
- **Entire agreement.** These Terms, together with any policies or supplemental terms expressly incorporated by reference, are the entire agreement between you and Atlas regarding the Service.

## 18. Contact

Atlas Forecasting, Inc.

- https://atlasforecasting.com/
- support@atlasforecasting.com
`.trim()
